import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  DatePicker,
  message,
  Row,
  Col,
  Space,
  Popconfirm,
  InputNumber,
  Image,
  Upload
} from 'antd';
import dayjs from 'dayjs';
import {
  FileTextOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined
} from '@ant-design/icons';
import './index.css';
import axios from 'axios';


const { Option } = Select;
const { TextArea } = Input;

const OrderFill = () => {
  const [form] = Form.useForm();
  const [modalForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('add'); // add, edit, view
  const [currentRecord, setCurrentRecord] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [antiCheatRecords, setAntiCheatRecords] = useState([]);

  // 店铺和接待助理的options
  const [shops, setShops] = useState([]);
  const [placeOrderUsers, setPlaceOrderUsers] = useState([]);

  // 客服和师傅的options
  const [serviceUsers, setServiceUsers] = useState([]);
  const [masterUsers, setMasterUsers] = useState([]);

  // 店铺的options
  const [storeOptions, setStoreOptions] = useState([]);


  // 主表数据 orders
  const [dataSource, setDataSource] = useState([]);

  // 搜索form
  const [searchForm] = Form.useForm();

  const columns = [
    {
      title: '日期',
      dataIndex: 'order_date',
      key: 'order_date',
      width: 100,
    },
    {
      title: '订单类型',
      dataIndex: 'order_type',
      key: 'order_type',
      width: 100,
    },
    {
      title: '所属店铺',
      dataIndex: 'shop_name',  // 使用显示名称
      key: 'shop_name',
      width: 120,
    },
    {
      title: '类型',
      dataIndex: 'g_type_display',  // 使用显示名称
      key: 'g_type_display',
      width: 80,
    },
    {
      title: '接待助理',
      dataIndex: 'receptionist_name',  // 使用显示名称
      key: 'receptionist_name',
      width: 100,
    },
    {
      title: '被查证人',
      dataIndex: 'verifiedPerson',
      key: 'verifiedPerson',
      width: 100,
    },
    {
      title: '被查证人微信号',
      dataIndex: 'wechatId',
      key: 'wechatId',
      width: 150,
    },
    {
      title: '师傅账号',
      dataIndex: 'master_name',  // 使用显示名称
      key: 'master_name',
      width: 100,
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      width: 120,
    },
    {
      title: '订单总金额',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 120,
      render: (amount) => `¥${amount}`
    },
    {
      title: '支付方式',
      dataIndex: 'payment_method',
      key: 'payment_method',
      width: 100,
    },
    {
      title: '操作',
      key: 'action',
      width: 260,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
          >
            查看
          </Button>
          <Popconfirm
            title="确定要删除这条记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // 搜索功能
  // 处理搜索
  const handleSearch = () => {
    // 使用第一页加载数据
    loadData({ page: 1 });
  };


  // 分页处理
  const handleTableChange = async (pagination) => {
    setPagination(pagination);

    try {
      setLoading(true);
      const response = await axios.get('/api/orders', {
        params: {
          search: searchText,
          page: pagination.current,
          per_page: pagination.pageSize
        }
      });

      if (response.data.success) {
        setDataSource(response.data.data);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };


  // 新增
  const handleAdd = () => {
    setModalType('add');
    setCurrentRecord(null); // 清空当前记录
    setAntiCheatRecords([]); // 清空反作弊记录
    modalForm.resetFields();
    setModalVisible(true);
  };


  // 编辑
  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentRecord(record);


    const rawDate = record.order_date || record.date || record.orderDate;
    const date =
      rawDate && dayjs(rawDate).isValid()
        ? dayjs(rawDate)
        : (rawDate ? dayjs(rawDate, 'YYYY/MM/DD') : null);



    const formValues = {
      ...record,
      // 表单字段名 -> 后端字段映射与类型转换
      date,                                            // DatePicker 需要 dayjs
      orderType: record.order_type,                    // 订单类型
      assistant: Number(record.receptionist),  // 接待助理（使用用户ID）
      masterAccount: Number(record.master_account), // 师傅账号（使用用户ID）
      // 该下拉当前用的是店铺“名称”作为 value，后端给的是 id 和 name，优先用 name
      store: record.shop_name || record.store || record.shop_id,
      remarks: record.remark,                          // 备注
      // 界面期望中文显示，如“查挂/查G”，后端有 g_type_display
      type: record.g_type,
      serviceCommission: record.serviceCommission,    // 若后端有此字段则回填，否则保持默认
      paymentMethod: record.payment_method,            // 支付方式
      totalAmount: record.total_amount               // 订单总金额
    };

    // 子表赋值（反作弊记录）
    const children = record.anti_cheat_records || record.antiCheatRecords || [];
    const toIsoOrNull = (v) => {
      if (!v) return null;
      // 支持已是 ISO 字符串、Date、dayjs
      if (typeof v === 'string') return v;
      if (v.toISOString) return v.toISOString();
      if (v.toDate) return v.toDate().toISOString();
      return v;
    };
    const mappedChildren = children.map((item, i) => ({
      id: item.id ?? `${record.id || 'tmp'}-${i}`,     // 保证 rowKey 稳定
      anti_cheat_code: item.anti_cheat_code ?? item.code ?? '',
      shop_id: item.shop_id ?? null,
      checked_person: item.checked_person ?? item.user_id ?? '',
      price: item.price ?? null,
      machine_code: item.machine_code ?? '',
      pc_setup_date: toIsoOrNull(item.pc_setup_date),
      game_log_date: toIsoOrNull(item.game_log_date),
      start_check_time: toIsoOrNull(item.start_check_time),
      end_check_time: toIsoOrNull(item.end_check_time),
      result: item.result ?? '',
      remarks: item.remarks ?? item.remark ?? '',
      // 图片字段：可为相对URL或base64，表格中直接预览
      work_area_image: item.work_area_image ?? '',
      host_back_image: item.host_back_image ?? '',
      host_interface_image: item.host_interface_image ?? '',
      host_internal_image1: item.host_internal_image1 ?? '',
      host_internal_image2: item.host_internal_image2 ?? '',
      system_log_image: item.system_log_image ?? '',
      master_share: item.master_share ?? null,
      wechat_id: item.wechat_id ?? ''
    }));
    setAntiCheatRecords(mappedChildren);
    console.log('mappedChildren', mappedChildren);
    modalForm.setFieldsValue(formValues);
    setModalVisible(true);
  };


  // 查看
  const handleView = async (record) => {
    setModalType('view');
    setCurrentRecord(record);

    // 日期统一为 dayjs
    const rawDate = record.order_date || record.date || record.orderDate;
    const date =
      rawDate && dayjs(rawDate).isValid()
        ? dayjs(rawDate)
        : (rawDate ? dayjs(rawDate, 'YYYY/MM/DD') : null);

    // 主表字段映射（与 handleEdit 保持一致）
    const formValues = {
      ...record,
      date,
      orderType: record.order_type,
      assistant: Number(record.receptionist),  // 接待助理（使用用户ID）
      masterAccount: Number(record.master_account), // 师傅账号（使用用户ID）
      store: record.shop_name || record.store || record.shop_id,
      remarks: record.remark,
      type: record.g_type_display || record.g_type,
      serviceCommission: record.serviceCommission,
      paymentMethod: record.payment_method,
      totalAmount: record.total_amount
    };

    // 子表：优先用列表里带过来的；没有则请求详情补齐
    let children = record.anti_cheat_records || record.antiCheatRecords || [];
    if (!children.length && record.id) {
      try {
        const { data } = await axios.get(`/api/orders/${record.id}`);
        if (data?.success && data.data) {
          children = data.data.anti_cheat_records || data.data.antiCheatRecords || [];
        }
      } catch (e) {
        // 忽略详情拉取失败，保持空子表
      }
    }

    const toIsoOrNull = (v) => {
      if (!v) return null;
      if (typeof v === 'string') return v;
      if (v.toISOString) return v.toISOString();
      if (v.toDate) return v.toDate().toISOString();
      return v;
    };

    const mappedChildren = (children || []).map((item, i) => ({
      id: item.id ?? `${record.id || 'tmp'}-v-${i}`,
      anti_cheat_code: item.anti_cheat_code ?? item.code ?? '',
      shop_id: item.shop_id ?? null,
      checked_person: item.checked_person ?? item.user_id ?? '',
      price: item.price ?? null,
      machine_code: item.machine_code ?? '',
      pc_setup_date: toIsoOrNull(item.pc_setup_date),
      game_log_date: toIsoOrNull(item.game_log_date),
      start_check_time: toIsoOrNull(item.start_check_time),
      end_check_time: toIsoOrNull(item.end_check_time),
      result: item.result ?? '',
      remarks: item.remarks ?? item.remark ?? '',
      work_area_image: item.work_area_image ?? '',
      host_back_image: item.host_back_image ?? '',
      host_interface_image: item.host_interface_image ?? '',
      host_internal_image1: item.host_internal_image1 ?? '',
      host_internal_image2: item.host_internal_image2 ?? '',
      system_log_image: item.system_log_image ?? '',
      master_share: item.master_share ?? null,
      wechat_id: item.wechat_id ?? ''
    }));

    setAntiCheatRecords(mappedChildren);
    modalForm.setFieldsValue(formValues);
    setModalVisible(true);
  };

  // 删除
  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`/api/orders/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        }
      });
      if (!res.data?.success) throw new Error(res.data?.message || '删除失败');
      message.success('删除成功');
      // 重新拉取列表，保持分页一致
      await loadData?.();
    } catch (e) {
      console.error('删除失败:', e);
      message.error(e.message || '删除失败');
    }
  };


  // 生成防伪码的函数
  const generateAntiCheatCode = () => {
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
    const randomNum = Math.floor(Math.random() * 900000) + 100000; // 6位随机数
    return `sp${dateStr}${timeStr}${randomNum}`;
  };

  // 添加反作弊记录
  const addAntiCheatRecord = () => {
    const newRecord = {
      id: Date.now(), // 临时ID
      anti_cheat_code: generateAntiCheatCode(),
      shop_id: null,
      checked_person: '',
      price: null,
      machine_code: '',
      pc_setup_date: null,
      game_log_date: null,
      start_check_time: null,
      end_check_time: null,
      result: '',
      remarks: '',
      work_area_image: '',
      host_back_image: '',
      host_interface_image: '',
      host_internal_image1: '',
      host_internal_image2: '',
      system_log_image: '',
      master_share: 25,
      wechat_id: ''
    };
    setAntiCheatRecords([...antiCheatRecords, newRecord]);
  };

  // 4. 更新记录的函数
  const updateAntiCheatRecord = (index, field, value) => {
    setAntiCheatRecords(prev => {
      const newRecords = [...prev];
      newRecords[index] = {
        ...newRecords[index],
        [field]: value
      };
      return newRecords;
    });

    console.log('更新后的记录', antiCheatRecords);
  };


  // 统一加载数据方法
  const loadData = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      // 获取表单当前值
      const formValues = searchForm.getFieldsValue();

      const { search, master_service, date } = formValues;
      
      // 构建请求参数
      const requestParams = {
        page: params.page || pagination.current,
        per_page: params.per_page || pagination.pageSize,
        // 添加搜索条件
        ...(search && String(search).trim() && { search: String(search).trim() }),
        // 添加师傅/客服筛选条件
        ...(master_service && { master_service }),
        // 添加日期范围筛选条件
        ...(date && date.length === 2 && {
          start_date: dayjs(date[0]).format('YYYY-MM-DD'),
          end_date: dayjs(date[1]).format('YYYY-MM-DD')
        }),
        ...params // 合并额外的参数
      };
      

      const res = await axios.get('/api/orders', { params: requestParams });
      
      if (res.data?.success) {
        setDataSource(res.data.data);
        setPagination(prev => ({
          ...prev,
          current: requestParams.page,
          pageSize: requestParams.per_page,
          total: res.data.total || 0
        }));
      } else {
        throw new Error(res.data?.message || '加载失败');
      }
    } catch (error) {
      message.error(error.message || '加载失败');
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  }, [searchForm, pagination.current, pagination.pageSize]);



  // 重置搜索form
  const handleResetFilters = () => {
    searchForm.resetFields();
    // 使用第一页和默认页面大小加载数据
    loadData({ page: 1, per_page: pagination.pageSize });
  };

  // 7. 加载店铺和用户数据
  useEffect(() => {
    // 首次加载订单
    loadData();

    // 加载店铺数据
    axios.get('/api/storesoptions')
      .then(res => {
        setShops(res.data);
      })
      .catch(err => console.error('加载店铺失败:', err));

    // 加载place_orders用户数据
    axios.get('/api/place-orders/usersoptions')
      .then(res => {
        if (res.data.success) {
          setPlaceOrderUsers(res.data.data);
        }
      })
      .catch(err => console.error('加载用户失败:', err));

    // 加载接待助理和师傅账号数据
    axios.get('/api/users/rolesoptions')
      .then(res => {
        if (res.data.success) {
          setServiceUsers(res.data.data.service);
          setMasterUsers(res.data.data.master);
        }
      })
      .catch(err => console.error('加载用户角色失败:', err));
  }, []);



  // 删除反作弊记录
  const removeAntiCheatRecord = (index) => {
    const newRecords = antiCheatRecords.filter((_, i) => i !== index);
    setAntiCheatRecords(newRecords);
  };


  // 提交
  const handleSubmit = async (values) => {
    try {

      // 校验必填项
      const requiredFields = ['date', 'orderType', 'assistant', 'masterAccount', 'store', 'type', 'paymentMethod', 'totalAmount'];
      const missingFields = requiredFields.filter(field => {
        const value = values[field];
        return value === undefined || value === null || value === '';
      });

      if (missingFields.length > 0) {
        message.error(`请填写必填项: ${missingFields.join(', ')}`);
        return;
      }

      // 校验子表必填项
      const requiredChildFields = ['work_area_image'];
      for (let i = 0; i < antiCheatRecords.length; i++) {
        const record = antiCheatRecords[i];
        const missingChildFields = requiredChildFields.filter(field => {
          const value = record[field];

          // 检查 File 对象
          if (value instanceof File) {
            return value.size === 0; // 文件大小为0表示无效
          }

          // 检查字符串
          if (typeof value === 'string') {
            return !value.trim();
          }

          // 其他情况
          return !value;
        });

        if (missingChildFields.length > 0) {
          message.error(`第${i + 1}行缺少必填项: ${missingChildFields.join(', ')}`);
          return;
        }
      }

      // 修复后的 toIsoDate
      const toIsoDate = (v) => {
        if (!v) return null;
        try {
          // 优先使用 format 方法，避免时区转换问题
          if (v.format && typeof v.format === 'function') {
            return v.format('YYYY-MM-DD');
          } else if (typeof v === 'string') {
            return v;
          } else if (v instanceof Date) {
            // 使用本地时间，避免时区问题
            const year = v.getFullYear();
            const month = String(v.getMonth() + 1).padStart(2, '0');
            const day = String(v.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          } else if (v.toDate && typeof v.toDate === 'function') {
            // 最后才使用 toDate，并手动处理时区
            const date = v.toDate();
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }
          return null;
        } catch (error) {
          console.warn('Date conversion error:', error, v);
          return null;
        }
      };

      const isEdit = modalType === 'edit' && currentRecord?.id;

      // 主表字段
      const payloadBase = {
        order_date: toIsoDate(values.date),
        order_type: values.orderType,
        total_amount: Number(values.totalAmount ?? 0),
        receptionist: values.assistant,
        master_account: values.masterAccount,
        shop_id: (() => {
          if (typeof values.store === 'number') {
            return values.store;
          }
          const shop = shops.find(s => s.label === values.store);
          return shop ? shop.value : null;
        })(),
        remark: values.remarks ?? '',
        g_type: values.type,
        payment_method: values.paymentMethod
      };

        console.log('payloadBase', payloadBase)

      // 处理文件字段
      const fileFields = [
        'work_area_image',
        'host_back_image',
        'host_interface_image',
        'host_internal_image1',
        'host_internal_image2',
        'system_log_image'
      ];

      // 创建 FormData
      const fd = new FormData();

      // 添加主表字段
      Object.entries(payloadBase).forEach(([k, v]) => {
        if (v !== undefined && v !== null) {
          fd.append(k, String(v));
        }
      });

      // 处理子表数据 - 先清理文件字段，只保留非文件数据
      const children = (antiCheatRecords || []).map((item, idx) => {
        const cleaned = { ...item };

        // 处理店铺ID
        if (cleaned.shop_id && typeof cleaned.shop_id === 'string') {
          const shop = shops.find(s => s.label === cleaned.shop_id);
          cleaned.shop_id = shop ? shop.value : null;
        }

        // 移除文件字段，这些会单独添加到 FormData
        fileFields.forEach((field) => {
          delete cleaned[field];
        });

        return cleaned;
      });

      // 添加子表 JSON 数据
      fd.append('anti_cheat_records', JSON.stringify(children));

      // 添加文件到 FormData
      (antiCheatRecords || []).forEach((item, idx) => {
        fileFields.forEach((field) => {
          const file = item[field];
          if (file instanceof File) {
            console.log(`添加文件: ${field}_${idx}`, file);
            fd.append(`${field}_${idx}`, file, file.name || `${field}.jpg`);
          }
        });
      });

      // 调试：打印 FormData 内容
      console.log('FormData 内容:');
      for (let [key, value] of fd.entries()) {
        console.log(key, value);
      }

      const url = isEdit ? `/api/orders/${currentRecord.id}` : '/api/orders';
      const method = isEdit ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: fd
      });

      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || (isEdit ? '更新失败' : '创建失败'));

      message.success(isEdit ? '订单更新成功' : '订单创建成功');
      setModalVisible(false);
      modalForm.resetFields();
      setAntiCheatRecords([]);
      await loadData?.();
    } catch (error) {
      console.error(error);
      message.error(error.message || (modalType === 'edit' ? '更新失败' : '创建失败'));
    }
  };



  // 修改图片列的渲染方式
  const renderImageUploadCell = (field, label, required = false) => (text, record, index) => {

    // 换图的时候
    const handleChange = (info) => {
      const { fileList } = info;
      const currentFile = fileList[fileList.length - 1];
  
      if (currentFile && currentFile.originFileObj) {
        const fileObj = currentFile.originFileObj;
        // 为预览添加临时 URL（挂到对象上，便于复用）
        if (!fileObj.preview) {
          try {
            fileObj.preview = URL.createObjectURL(fileObj);
          } catch { }
        }
        updateAntiCheatRecord(index, field, fileObj);
      }
    };
  
    // 预览图片
    const handlePreview = async (file) => {
      // 如果是服务端 URL，直接用 url
      if (file.url) {
        window.open(file.url, '_blank');
        return;
      }
      // 如果是本地 File 对象，用 thumbUrl
      if (file.thumbUrl) {
        window.open(file.thumbUrl, '_blank');
        return;
      }
      // 其他情况尝试创建预览
      const v = record[field];
      if (v instanceof File) {
        const previewUrl = URL.createObjectURL(v);
        window.open(previewUrl, '_blank');
        // 注意：打开后立即释放可能导致无法查看，建议保留不释放
      }
    };
  
    // 基于当前单元格值构建 fileList（支持本地 File 和服务端 URL）
    const v = record[field];
    let fileList = [];
    if (v) {
      if (v instanceof File) {
        fileList = [{
          uid: `${field}_${index}`,
          name: v.name || 'image.jpg',
          status: 'done',
          thumbUrl: v.preview || undefined,
        }];
      } else if (typeof v === 'string') {
        const name = v.split('/').pop() || 'image.jpg';
        fileList = [{
          uid: `${field}_${index}`,
          name,
          status: 'done',
          url: v,
        }];
      }
    }
  
    return (
      <Upload
        accept="image/*"
        listType="picture-card"
        maxCount={1}
        beforeUpload={() => false}
        onChange={handleChange}
        showUploadList={{ 
          showRemoveIcon: true,    // 改为 true，显示删除按钮
          showPreviewIcon: true    // 改为 true，显示预览按钮
        }}
        onPreview={handlePreview}  // 添加预览处理函数
        fileList={fileList}
        // 删除
        onRemove={() => updateAntiCheatRecord(index, field, null)}
      >
        {fileList.length === 0 ? '+ 上传' : null}
      </Upload>
    );
  };


  // 挂载时候获取 shop的下拉list
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const res = await axios.get('/api/storesoptions', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setStoreOptions(res.data || []);
      } catch (e) {
        console.error('加载店铺列表失败:', e);
      }
    };
    fetchStores();
  }, []);


  // 防抖
  const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };



  return (
    <div className="order-fill-container">
      <Card
        title={
          <div className="order-fill-page-header">
            <FileTextOutlined className="page-icon" />
            <span>查G服务-订单填写</span>
          </div>
        }
        className="page-card"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            创建
          </Button>
        }
      >
        {/* 搜索框 */}
        <div className="search-container" style={{ marginBottom: 16, display: 'flex', gap: 12 }}>
          <Form layout="inline" onFinish={({ search, master_service, date }) => handleSearch(search, master_service, date)} form={searchForm}>
            <Form.Item name="search">
              <Input
                placeholder="搜索订单信息..."
                allowClear
                size="large"
                style={{ width: 300 }}
              />
            </Form.Item>
            <Form.Item name="master_service">
              <Select
                placeholder="选择师傅/客服"
                style={{ width: 200 }}
                size="large"
                allowClear
                showSearch
                optionFilterProp="children"
                options={[
                  ...serviceUsers.map(m => ({ value: m.value, label: `${m.label} (客服)` })),
                  ...masterUsers.map(cs => ({ value: cs.value, label: `${cs.label} (师傅)` }))
                ]}
              />
            </Form.Item>
            <Form.Item name="date">
              <DatePicker.RangePicker
                placeholder={['开始日期', '结束日期']}
                style={{ width: 200 }}
                size="large"
                format="YYYY-MM-DD"
              />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>搜索</Button>
                <Button onClick={handleResetFilters}>重置</Button>
              </Space>
            </Form.Item>
          </Form>

        </div>

        {/* 数据表格 */}
        <Table
          className="order-fill-table"
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}  // 添加loading状态
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: t => `共 ${t} 条记录`,
            onChange: (page, pageSize) => {
              loadData({ page, per_page: pageSize });
            },
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          tableLayout="auto"
          size="small"
        />
      </Card>

      {/* 新增/编辑/查看Modal */}
      <Modal
        title="订单填写"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        width='84%'
        maskClosable={false}  // 点击遮罩不关闭
        style={{
          top: '5%',
        }}
        footer={
          modalType === 'view' ? [
            <Button key="close" onClick={() => {
              // 重置 并关闭
              modalForm.resetFields()
              setModalVisible(false)
            }}
            >
              关闭
            </Button>
          ] : [
            <Button key="cancel" onClick={() => {
              // 重置 并关闭
              modalForm.resetFields()
              setModalVisible(false)
            }}
            >
              取消
            </Button>,
            <Button
              key="save"
              type="primary"
              onClick={debounce(async () => {
                try {
                  const values = await modalForm.validateFields();
                  await handleSubmit(values);
                } catch (error) {
                  // 表单验证失败，让表单自己显示验证错误，不显示额外提示
                  console.log('Form validation failed:', error);
                }
              }, 1000)} // 1秒防抖
            >
              保存
            </Button>
          ]
        }
      >
        <Form
          form={modalForm}
          layout="vertical"
          disabled={modalType === 'view'}
        >
          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="日期"
                name="date"
                rules={[{ required: true, message: '请选择日期' }]}
              >
                <DatePicker
                  style={{ width: '100%' }}
                  placeholder="年/月/日"
                  onChange={(date) => {
                    // 直接更新表单值，避免时区问题
                    modalForm.setFieldsValue({ date: date });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="订单类型"
                name="orderType"
                rules={[{ required: true, message: '请选择类型' }]}
              >
                <Select placeholder="请选择类型">
                  <Option value="散单">散单</Option>
                  <Option value="预存">预存</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="接待助理"
                name="assistant"
                rules={[{ required: true, message: '请选择接待助理' }]}
              >
                <Select
                  placeholder="请选择接待助理"
                  onChange={(value) => {
                      // console.log(value);
                      modalForm.setFieldsValue({ assistant: value });
                  }}
                >
                  {serviceUsers.map(user => (
                    <Option key={user.value} value={user.value}>
                      {user.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="师傅账号"
                name="masterAccount"
                rules={[{ required: true, message: '请选择师傅账号' }]}
              >
                <Select
                  placeholder="请选择师傅账号"
                  onChange={(value) => {
                      // console.log(value);
                      modalForm.setFieldsValue({ masterAccount: value });
                  }}
                >
                  {masterUsers.map(user => (
                    <Option key={user.value} value={user.value}>
                      {user.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={6}>
              <Form.Item
                label="所属店铺"
                name="store"
                rules={[{ required: true, message: '请选择店铺' }]}
              >
                <Select placeholder="请选择店铺" showSearch optionFilterProp="children">
                  {storeOptions.map(opt => (
                    <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="备注"
                name="remarks"
              >
                <Input placeholder="请输入备注" />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="查挂类型"
                name="type"
                rules={[{ required: true, message: '请选择类型' }]}
              >
                <Select placeholder="请选择类型">
                  <Option value="类型1">查挂</Option>
                  <Option value="类型2">售后</Option>
                  <Option value="类型3">系统调试</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="客服分成(元)"
                name="serviceCommission"
                initialValue={1}
              >
                <Input
                  type="number"
                  disabled
                  placeholder="请输入客服分成(元)"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="支付方式"
                name="paymentMethod"
                rules={[{ required: true, message: '请选择支付方式' }]}
              >
                <Select placeholder="请选择支付方式">
                  <Option value="支付宝">支付宝</Option>
                  <Option value="微信">微信</Option>
                  <Option value="银行卡">银行卡</Option>
                  <Option value="现金">现金</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="订单总金额"
                name="totalAmount"
                rules={[{ required: true, message: '请输入订单总金额' }]}
              >
                <Input
                  type="number"
                  placeholder="请输入订单总金额"
                  // disabled={modalType === 'view' || modalType === 'edit'}
                  disabled
                />
              </Form.Item>
            </Col>
          </Row>

          {/* 反作弊查询记录部分 */}
          <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h4>反作弊查询记录</h4>
              <Button type="primary" icon={<PlusOutlined />} size="small" onClick={addAntiCheatRecord}>
                添加记录
              </Button>
            </div>
            <Table
              columns={[
                {
                  title: '防伪码',
                  dataIndex: 'anti_cheat_code',
                  key: 'anti_cheat_code',
                  width: 200,
                  render: (text, record, index) => (
                    <Input
                      value={text}
                      onChange={(e) => updateAntiCheatRecord(index, 'anti_cheat_code', e.target.value)}
                      placeholder="自动生成"
                      size="small"
                      disabled
                    />
                  )
                },
                {
                  title: '所属店铺*',
                  dataIndex: 'shop_id',
                  key: 'shop_id',
                  width: 150,
                  render: (text, record, index) => (
                    <Select
                      value={text}
                      onChange={(value) => {
                        const newPrice = shops.find(shop => shop.value === value)?.unitprice || 0
                        
                        // 先模拟计算更新后的数据
                        const updatedRecords = [...antiCheatRecords]
                        updatedRecords[index] = {
                          ...updatedRecords[index],
                          shop_id: value,
                          price: newPrice
                        }

                        // 立即计算
                        const totalPrice = updatedRecords.reduce((sum, record) => {
                          // 确保 price 是数字类型
                          const price = Number(record.price) || 0
                          return sum + price
                        }, 0)
                        
                        // 赋值
                        modalForm.setFieldsValue({
                          totalAmount: parseFloat(totalPrice)
                        });
                        
                        // 然后异步更新实际状态
                        updateAntiCheatRecord(index, 'shop_id', value)
                        updateAntiCheatRecord(index, 'price', newPrice)
                      }}
                      
                      placeholder="选择店铺"
                      size="small"
                      style={{ width: '100%' }}
                      showSearch
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {shops.map(shop => (
                        <Option key={shop.value} value={shop.value}>
                          {shop.label}
                        </Option>
                      ))}
                    </Select>
                  )
                },
                {
                  title: '被查证人*',
                  dataIndex: 'checked_person',
                  key: 'checked_person',
                  width: 240,
                  render: (text, record, index) => (
                    <Select
                      value={text || undefined}  // 确保类型匹配
                      onChange={(value) => {
                        // 找到选中的用户信息
                        const selectedUser = placeOrderUsers.find(user => user.value === value);

                        // 合并更新，避免多次调用
                        const updates = {
                          checked_person: selectedUser.value || '',
                          wechat_id: selectedUser?.wechat_id || ''
                        };

                        // 一次性更新所有字段
                        Object.entries(updates).forEach(([field, val]) => {
                          updateAntiCheatRecord(index, field, val);
                        });

                      }}
                      placeholder="选择用户"
                      size="small"
                      style={{ width: '100%' }}
                      showSearch
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {placeOrderUsers.map(user => (
                        <Option key={user.value} value={user.value}>
                          {user.label}
                        </Option>
                      ))}
                    </Select>
                  )
                },
                {
                  title: '价格*',
                  dataIndex: 'price',
                  key: 'price',
                  width: 120,
                  render: (text, record, index) => (
                    <InputNumber
                      value={text}
                      disabled
                      onChange={(value) => {
                        // 先模拟计算更新后的数据
                        const updatedRecords = [...antiCheatRecords]

                        // 立即计算
                        const totalPrice = updatedRecords.reduce((sum, record) => {
                          // 确保 price 是数字类型
                          const price = Number(record.price) || 0
                          return sum + price
                        }, 0)
                        
                        // 赋值
                        modalForm.setFieldsValue({
                          totalAmount: parseFloat(totalPrice)
                        });


                        updateAntiCheatRecord(index, 'price', value)}
                      }
                      placeholder="0.00"
                      size="small"
                      style={{ width: '100%' }}
                      precision={2}
                      min={0}
                    />
                  )
                },
                {
                  title: '此次被查机器码*',
                  dataIndex: 'machine_code',
                  key: 'machine_code',
                  width: 150,
                  render: (text, record, index) => (
                    <Input
                      value={text}
                      onChange={(e) => updateAntiCheatRecord(index, 'machine_code', e.target.value)}
                      placeholder="输入机器码"
                      size="small"
                    />
                  )
                },
                {
                  title: 'pc装机时间',
                  dataIndex: 'pc_setup_date',
                  key: 'pc_setup_date',
                  width: 150,
                  render: (text, record, index) => (
                    <DatePicker
                      value={text ? dayjs(text) : null}
                      onChange={(date) => updateAntiCheatRecord(index, 'pc_setup_date', date ? date.format('YYYY-MM-DD HH:mm:ss') : null)}
                      placeholder="选择时间"
                      size="small"
                      style={{ width: '100%' }}
                      showTime
                    />
                  )
                },
                {
                  title: '游戏日志时间*',
                  dataIndex: 'game_log_date',
                  key: 'game_log_date',
                  width: 150,
                  render: (text, record, index) => (
                    <DatePicker
                      value={text ? dayjs(text) : null}
                      onChange={(date) => updateAntiCheatRecord(index, 'game_log_date', date ? date.format('YYYY-MM-DD HH:mm:ss') : null)}
                      placeholder="选择时间"
                      size="small"
                      style={{ width: '100%' }}
                      showTime
                    />
                  )
                },
                {
                  title: '开始查证时间*',
                  dataIndex: 'start_check_time',
                  key: 'start_check_time',
                  width: 150,
                  render: (text, record, index) => (
                    <DatePicker
                      value={text ? dayjs(text) : null}
                      onChange={(date) => updateAntiCheatRecord(index, 'start_check_time', date ? date.format('YYYY-MM-DD HH:mm:ss') : null)}
                      placeholder="选择时间"
                      size="small"
                      style={{ width: '100%' }}
                      showTime
                    />
                  )
                },
                {
                  title: '结束查证时间*',
                  dataIndex: 'end_check_time',
                  key: 'end_check_time',
                  width: 150,
                  render: (text, record, index) => (
                    <DatePicker
                      value={text ? dayjs(text) : null}
                      onChange={(date) => updateAntiCheatRecord(index, 'end_check_time', date ? date.format('YYYY-MM-DD HH:mm:ss') : null)}
                      placeholder="选择时间"
                      size="small"
                      style={{ width: '100%' }}
                      showTime
                    />
                  )
                },
                {
                  title: '结果*',
                  dataIndex: 'result',
                  key: 'result',
                  width: 120,
                  render: (text, record, index) => (
                    <Select
                      value={text}
                      onChange={(value) => updateAntiCheatRecord(index, 'result', value)}
                      placeholder="选择结果"
                      size="small"
                      style={{ width: '100%' }}
                    >
                      <Option value="正常">正常</Option>
                      <Option value="疑似">疑似</Option>
                      <Option value="开挂">开挂</Option>
                    </Select>
                  )
                },
                {
                  title: '备注事宜补充*',
                  dataIndex: 'remarks',
                  key: 'remarks',
                  width: 150,
                  render: (text, record, index) => (
                    <TextArea
                      value={text}
                      onChange={(e) => updateAntiCheatRecord(index, 'remarks', e.target.value)}
                      placeholder="输入备注"
                      size="small"
                      rows={2}
                    />
                  )
                },
                {
                  title: '工作区域整体图*',
                  dataIndex: 'work_area_image',
                  key: 'work_area_image',
                  width: 150,
                  render: renderImageUploadCell('work_area_image', '工作区域整体图', true)
                },
                {
                  title: '主机背板一',
                  dataIndex: 'host_back_image',
                  key: 'host_back_image',
                  width: 120,
                  render: renderImageUploadCell('host_back_image', '主机背板一')
                },
                {
                  title: '主机接口详情图二',
                  dataIndex: 'host_interface_image',
                  key: 'host_interface_image',
                  width: 120,
                  render: renderImageUploadCell('host_interface_image', '主机接口详情图二')
                },
                {
                  title: '主机内部硬件图一',
                  dataIndex: 'host_internal_image1',
                  key: 'host_internal_image1',
                  width: 120,
                  render: renderImageUploadCell('host_internal_image1', '主机内部硬件图一')
                },
                {
                  title: '主机内部硬件图二',
                  dataIndex: 'host_internal_image2',
                  key: 'host_internal_image2',
                  width: 120,
                  render: renderImageUploadCell('host_internal_image2', '主机内部硬件图二')
                },
                {
                  title: '系统日志详情图',
                  dataIndex: 'system_log_image',
                  key: 'system_log_image',
                  width: 120,
                  render: renderImageUploadCell('system_log_image', '系统日志详情图')
                },
                {
                  title: '师傅接单分润',
                  dataIndex: 'master_share',
                  key: 'master_share',
                  width: 120,
                  hidden: true,
                  render: (text, record, index) => (
                    <InputNumber
                      value={text !== null && text !== undefined ? text : 25}
                      // value={text}
                      // onChange={(value) => updateAntiCheatRecord(index, 'master_share', value)}
                      // defaultValue={25}
                      placeholder="0.00"
                      size="small"
                      style={{ width: '100%' }}
                      precision={2}
                      min={0}
                      disabled
                    />
                  )
                },
                {
                  title: '被查证人微信号',
                  dataIndex: 'wechat_id',
                  key: 'wechat_id',
                  width: 150,
                  render: (text, record, index) => (
                    <Input
                      value={text}
                      onChange={(e) => updateAntiCheatRecord(index, 'wechat_id', e.target.value)}
                      placeholder="输入微信号"
                      size="small"
                      disabled
                    />
                  )
                },
                {
                  title: '操作',
                  key: 'action',
                  width: 80,
                  render: (_, record, index) => (
                    <Button
                      type="link"
                      danger
                      onClick={() => removeAntiCheatRecord(index)}
                      size="small"
                    >
                      删除
                    </Button>
                  )
                }
              ]}
              dataSource={antiCheatRecords}
              rowKey={(record) => record.id ?? record.anti_cheat_code}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              tableLayout="auto"
              locale={{ emptyText: '暂无数据' }}
            />
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderFill;
